import { PLANNED_MEDICATION_FIELD_LABEL } from "@care-by-ella/contracts";
import { productInForce, productOf } from "@/lib/medication-picker";
import { useMedicationCatalogue } from "./PrescriptionOrderFields";

// TES-1185 — "styrka" and "behandlingsplan", the two parts of the planned
// medication the order's four fields (PrescriptionOrderFields) do not hold.
// TES-1241 — one component, so the care meeting's Recept box and the transfer
// review's Recept section ask for them the same way.
//
// TES-1292 — with the order's medication (`order`), the strength is picked
// from the catalogue entry once it is clinically approved; before that it is
// free text, as it was. A stored strength the entry does not list stays
// visible and selectable, marked as outside the catalogue: it is the
// doctor's earlier entry, and it is not this field's to drop. The strength
// is not part of the deviation rule (contracts catalogueDeviations).
export function PlannedMedicationExtraFields({
  idPrefix,
  strength,
  plan,
  onStrengthChange,
  onPlanChange,
  disabled,
  order,
}: {
  idPrefix: string;
  strength: string;
  plan: string;
  onStrengthChange: (v: string) => void;
  onPlanChange: (v: string) => void;
  disabled?: boolean;
  order?: { medication: string; medicationVersionId?: string };
}) {
  const catalogue = useMedicationCatalogue();
  const product = order ? productOf(catalogue, order) : null;
  const strengths =
    product && productInForce(product) ? product.current.strengths : null;
  const current = strength.trim();
  return (
    <div className="flex flex-wrap items-start gap-sm">
      <div className="flex w-40 flex-col gap-xs">
        <label
          className="text-xs text-textSecondary"
          htmlFor={`${idPrefix}-strength`}
        >
          {PLANNED_MEDICATION_FIELD_LABEL.strength}
        </label>
        {strengths ? (
          <select
            id={`${idPrefix}-strength`}
            value={current}
            onChange={(e) => onStrengthChange(e.target.value)}
            disabled={disabled}
            className="rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm"
          >
            <option value="">Välj</option>
            {strengths.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
            {current && !strengths.includes(current) ? (
              <option value={current}>{current} (utanför katalogen)</option>
            ) : null}
          </select>
        ) : (
          <input
            id={`${idPrefix}-strength`}
            value={strength}
            onChange={(e) => onStrengthChange(e.target.value)}
            placeholder="t.ex. 2,4 mg"
            maxLength={64}
            disabled={disabled}
            className="rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm"
          />
        )}
      </div>
      <div className="flex min-w-[16rem] flex-1 flex-col gap-xs">
        <label
          className="text-xs text-textSecondary"
          htmlFor={`${idPrefix}-plan`}
        >
          {PLANNED_MEDICATION_FIELD_LABEL.plan}
        </label>
        <textarea
          id={`${idPrefix}-plan`}
          value={plan}
          onChange={(e) => onPlanChange(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={disabled}
          className="rounded-md border border-surfaceMuted bg-background px-sm py-xs text-sm"
        />
      </div>
    </div>
  );
}
